import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import SummaryCard from '../components/SummaryCard';
import { getAllocations, getFiscalYears } from '../services/api';
import { useToken } from '../hooks/useToken';
import { useCurrentUser, isOnyxFC } from '../context/UserContext';
import type { Allocation, FiscalYear } from '../types';

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

const FUND_TYPE_COLORS: Record<string, string> = {
  Federal: 'bg-blue-100 text-blue-700',
  State:   'bg-purple-100 text-purple-700',
  Other:   'bg-gray-100 text-gray-600',
};

export default function FundPools() {
  const getToken = useToken();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const onyxFC = isOnyxFC(user);

  const [allocations, setAllocations] = useState<Allocation[]>([]);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [selectedFYId, setSelectedFYId] = useState<string>('');
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getToken()
      .then(async (t) => {
        const fys = await getFiscalYears(t).catch(() => [] as FiscalYear[]);
        setFiscalYears(fys);
        const current = fys.find((f) => f.is_current)?.fiscal_year_id;
        const currentId = current ? String(current) : '';
        setSelectedFYId(currentId);
        return loadAllocations(t, currentId);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAllocations(token: string, fyId: string) {
    const res = await getAllocations(token, { fiscalYearId: fyId ? Number(fyId) : undefined });
    setAllocations(res.data);
    setTotalAllocation(res.data.reduce((sum, a) => sum + Number(a.allocated_amount), 0));
    setTotalPaid(res.data.reduce((sum, a) => sum + (Number(a.allocated_amount) - Number(a.remaining_balance)), 0));
  }

  async function handleFYChange(fyId: string) {
    setSelectedFYId(fyId);
    setLoading(true);
    try {
      const token = await getToken();
      await loadAllocations(token, fyId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout pageTitle="Fund Pools">
      <p className="text-sm text-gray-500 mb-5">BHSD fund pool allocations by project and fiscal year</p>

      {/* Summary + controls row */}
      <div className="flex items-start gap-4 mb-6">
        <div className="flex gap-4 flex-1">
          <SummaryCard label="Total Allocation" value={formatCurrency(totalAllocation)} />
          <SummaryCard label="Total Paid" value={formatCurrency(totalPaid)} />
        </div>
        <div className="flex items-center gap-3 mt-auto mb-0">
          {fiscalYears.length > 0 && (
            <select
              value={selectedFYId}
              onChange={(e) => handleFYChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Years</option>
              {fiscalYears.map((fy) => (
                <option key={fy.fiscal_year_id} value={fy.fiscal_year_id}>{fy.label}</option>
              ))}
            </select>
          )}
          {onyxFC && (
            <button
              onClick={() => navigate('/allocations/new')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
            >
              + New Allocation
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        {error ? (
          <div className="px-5 py-8 text-center text-sm text-red-600">{error}</div>
        ) : loading ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">Loading...</div>
        ) : allocations.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">No allocations found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fund Pool</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fund Type</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Project</th>
                <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Fiscal Year</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Allocation</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Paid</th>
                <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Pending</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a) => {
                const fundType = a.fund_type ?? 'Other';
                const typeClass = FUND_TYPE_COLORS[fundType] ?? FUND_TYPE_COLORS['Other'];
                return (
                  <tr key={a.allocation_id} className="border-b border-gray-50 hover:bg-gray-50 last:border-0">
                    <td className="px-5 py-3">
                      <span className="font-medium text-gray-900">{a.fund_pool_description ?? a.fund_pool_code}</span>
                      <span className="ml-2 text-xs text-gray-400">{a.fund_pool_code}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeClass}`}>
                        {fundType}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-gray-600">{a.project_name}</td>
                    <td className="px-5 py-3 text-gray-600">{a.fiscal_year_label}</td>
                    <td className="px-5 py-3 text-right text-gray-900">{formatCurrency(Number(a.allocated_amount))}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{formatCurrency(Number(a.allocated_amount) - Number(a.remaining_balance))}</td>
                    <td className="px-5 py-3 text-right text-gray-600">{formatCurrency(Number(a.pending_amount) || 0)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
